import logger from '../utils/logger';
import { themes, getThemeById } from '../data/themes';

/**
 * Theme Manager Interface
 */
class ThemeService {
  constructor() {
    this.currentTheme = this.loadTheme();
    this.customization = this.loadCustomization();
    this.listeners = [];
  }

  /**
   * Get current theme
   * @returns {Object} Current theme object
   */
  getCurrentTheme() {
    return this.currentTheme;
  }

  /**
   * Get current customization
   * @returns {Object} Current customization object
   */
  getCustomization() {
    return this.customization;
  }

  /**
   * Load theme from localStorage
   * @returns {Object} Theme object
   */
  loadTheme() {
    try {
      const savedThemeId = localStorage.getItem('menuThemeId') || 'coffeecloud-default';
      const theme = getThemeById(savedThemeId);
      return theme || themes[0];
    } catch (error) {
  logger.error('Error loading theme:', error);
      return themes[0];
    }
  }

  /**
   * Load customization from localStorage
   * @returns {Object} Customization object
   */
  loadCustomization() {
    try {
      const saved = localStorage.getItem('menuCustomization');
      if (saved) {
        const parsed = JSON.parse(saved);
        return {
          themeId: parsed.themeId || 'coffee-classic',
          colors: parsed.colors || {},
          typography: parsed.typography || {},
          layout: parsed.layout || {},
          showCategories: parsed.showCategories !== undefined ? parsed.showCategories : true,
          showDescriptions: parsed.showDescriptions !== undefined ? parsed.showDescriptions : true,
          showImages: parsed.showImages !== undefined ? parsed.showImages : true,
          productCardStyle: parsed.productCardStyle || 'horizontal',
          cartStyle: parsed.cartStyle || 'bottom-sheet',
          animationStyle: parsed.animationStyle || 'smooth'
        };
      }
    } catch (error) {
  logger.error('Error loading customization:', error);
    }

    return {
      themeId: 'coffee-classic',
      colors: {},
      typography: {},
      layout: {},
      showCategories: true,
      showDescriptions: true,
      showImages: true,
      productCardStyle: 'horizontal',
      cartStyle: 'bottom-sheet',
      animationStyle: 'smooth'
    };
  }

  /**
   * Save theme to localStorage
   * @param {Object} theme - Theme object
   */
  saveTheme(theme) {
    try {
      localStorage.setItem('menuThemeId', theme.id);
      this.currentTheme = theme;
      this.notifyListeners();
    } catch (error) {
  logger.error('Error saving theme:', error);
    }
  }

  /**
   * Save customization to localStorage
   * @param {Object} customization - Customization object
   */
  saveCustomization(customization) {
    try {
      localStorage.setItem('menuCustomization', JSON.stringify(customization));
      this.customization = customization;
      this.notifyListeners();
    } catch (error) {
  logger.error('Error saving customization:', error);
    }
  }

  /**
   * Apply theme to DOM
   * @param {string} themeId - Theme ID
   */
  applyTheme(themeId) {
    const theme = getThemeById(themeId);
    if (!theme) return;

    this.currentTheme = theme;
    this.saveTheme(theme);

    // Apply CSS variables
    const root = document.documentElement;
    const colors = { ...theme.colors, ...this.customization.colors };
    const typography = { ...theme.typography, ...this.customization.typography };
    const layout = { ...theme.layout, ...this.customization.layout };

    // Apply colors
    Object.entries(colors).forEach(([key, value]) => {
      root.style.setProperty(`--color-${key}`, value);
    });

    // Apply typography
    root.style.setProperty('--font-family', typography.fontFamily);
    root.style.setProperty('--font-heading', typography.headingFont);
    root.style.setProperty('--font-body', typography.bodyFont);
    root.style.setProperty('--font-size-base', `${typography.baseSize}px`);
    root.style.setProperty('--font-size-heading', `${typography.headingSize}px`);
    root.style.setProperty('--line-height', typography.lineHeight);
    root.style.setProperty('--font-weight', typography.fontWeight);

    // Apply layout
    root.style.setProperty('--border-radius', layout.borderRadius);
    root.style.setProperty('--spacing', layout.spacing);
    root.style.setProperty('--shadow', layout.shadow);
    root.style.setProperty('--animation', layout.animation);

    // Notify listeners of theme change
    this.notifyListeners();
  }

  /**
   * Initialize default theme for first-time visitors
   */
  initializeDefaultTheme() {
    const hasExistingTheme = localStorage.getItem('menuThemeId');
    if (!hasExistingTheme) {
      this.applyTheme('coffeecloud-default');
    }
  }

  /**
   * Update customization
   * @param {Object} updates - Customization updates
   */
  updateCustomization(updates) {
    const newCustomization = { ...this.customization, ...updates };
    this.saveCustomization(newCustomization);
    this.applyTheme(newCustomization.themeId);
  }

  /**
   * Reset customization to default
   */
  resetCustomization() {
    const defaultCustomization = {
      themeId: 'coffee-classic',
      colors: {},
      typography: {},
      layout: {},
      showCategories: true,
      showDescriptions: true,
      showImages: true,
      productCardStyle: 'horizontal',
      cartStyle: 'bottom-sheet',
      animationStyle: 'smooth'
    };
    this.saveCustomization(defaultCustomization);
    this.applyTheme(defaultCustomization.themeId);
  }

  /**
   * Export theme configuration
   * @returns {string} JSON string of theme configuration
   */
  exportTheme() {
    const config = {
      theme: this.currentTheme,
      customization: this.customization,
      exportDate: new Date().toISOString()
    };
    return JSON.stringify(config, null, 2);
  }

  /**
   * Import theme configuration
   * @param {string} configJson - JSON string of theme configuration
   * @returns {boolean} Success status
   */
  importTheme(configJson) {
    try {
      const config = JSON.parse(configJson);
      if (config.theme && config.customization) {
        this.saveTheme(config.theme);
        this.saveCustomization(config.customization);
        this.applyTheme(config.theme.id);
        return true;
      }
      return false;
    } catch (error) {
  logger.error('Error importing theme:', error);
      return false;
    }
  }

  /**
   * Add listener for theme changes
   * @param {Function} listener - Listener function
   */
  addListener(listener) {
    this.listeners.push(listener);
  }

  /**
   * Remove listener
   * @param {Function} listener - Listener function
   */
  removeListener(listener) {
    this.listeners = this.listeners.filter(l => l !== listener);
  }

  /**
   * Notify all listeners
   */
  notifyListeners() {
    this.listeners.forEach(listener => {
      try {
        listener(this.currentTheme, this.customization);
      } catch (error) {
  logger.error('Error in theme listener:', error);
      }
    });
  }
}

// Create singleton instance
const themeService = new ThemeService();

/**
 * Get Tailwind classes based on customization
 * @param {Object} customization - Customization object
 * @returns {Object} Tailwind classes object
 */
export const getTailwindClasses = (customization) => {
  const classes = {
    productCard: 'bg-white rounded-lg shadow-md p-4 transition-all duration-300',
    cartButton: 'bg-blue-600 text-white rounded-full p-3 shadow-lg',
    categoryButton: 'px-4 py-2 rounded-full text-sm font-medium transition-colors',
    addButton: 'bg-green-600 text-white rounded-full p-2 shadow-md hover:bg-green-700'
  };

  // Apply product card style
  if (customization.productCardStyle === 'vertical') {
    classes.productCard += ' flex flex-col';
  } else {
    classes.productCard += ' flex flex-row items-center space-x-4';
  }

  // Apply animation style
  if (customization.animationStyle === 'bounce') {
    classes.productCard += ' hover:scale-105 hover:rotate-1';
  } else if (customization.animationStyle === 'slide') {
    classes.productCard += ' hover:translate-x-2';
  } else {
    classes.productCard += ' hover:shadow-lg';
  }

  return classes;
};

/**
 * Get grid layout classes based on theme and customization
 * @param {Object} theme - Theme object
 * @param {Object} customization - Customization object
 * @returns {string} Grid layout classes
 */
export const getGridClasses = (theme, customization) => {
  // Use theme layout properties first, then fallback to customization
  const layout = theme?.layout || {};
  const cardStyle = layout.productCardStyle || customization?.productCardStyle || 'vertical';
  const gridColumns = layout.gridColumns || 'grid-cols-2';
  const layoutStyle = layout.layoutStyle || 'grid';
  
  // Handle special layout styles
  if (layoutStyle === 'sheet') {
    return 'space-y-0'; // Sheet style handles its own spacing
  }
  
  if (layoutStyle === 'featured') {
    return 'space-y-6'; // Featured layout with spacing
  }
  
  if (cardStyle === 'vertical') {
    return `grid ${gridColumns} gap-4`;
  }
  
  // For horizontal cards, use the specific spacing class
  return gridColumns.includes('space-y') ? gridColumns : 'space-y-4';
};

export { themeService };
